<!--
  Preview fallbacks -- card #243 ("P0: Artifi mockup fallbacks -- when Artifi
  can't render a SKU/style, never block product setup; build 3 fallbacks",
  https://trello.com/c/jFoJk0j5).

  The Stage mounts this where the Artifi preview would be, when Artifi cannot
  draw it: the style is unsupported (GH #4443), or the render answered
  sku_not_configured / render_failed. It shows:

    1. Your image  -- the color's image: the distributor's upload, else the
                      supplier photo, else a tinted garment. The upload
                      CONTROL is not here: ColorImageStrip offers it for every
                      selected color, rendered or not (Jake, 2026-10-28).
    2. Zone diagram -- an SVG zone overlay on a generic garment tinted to the
                      color: where each placement's decoration sits.
    3. Open canvas  -- arrange the decorations freely and save the result as
                      the color's preview image (OpenCanvasPlacement).

  2 and 3 are behind the SuperAdmin switch `toolsEnabled` (default OFF, Jake
  2026-10-28): while it is off there are no tabs and only 1 is reachable,
  also if stale mode state says otherwise.

  All are VISUAL ONLY and the surface says so: production artwork is
  submitted separately and nothing here changes it.

  Props in, events out: the Stage owns the data (marks, the color's upload,
  the request status) and the store calls.
-->
<template>
    <div
        class="artifi-fallback"
        data-testid="stage-preview-fallback"
        :data-mode="shownMode"
        :data-reason="reason"
    >
        <p v-if="message" class="artifi-fallback-lead" data-testid="stage-fallback-lead">
            {{ message }}
        </p>
        <div
            v-if="toolsEnabled"
            class="artifi-fallback-modes"
            role="tablist"
            aria-label="Preview options"
            data-testid="stage-fallback-modes"
        >
            <button
                v-for="m in MODES"
                :key="m.id"
                type="button"
                role="tab"
                class="artifi-fallback-mode"
                :class="{ 'is-active': shownMode === m.id }"
                :aria-selected="shownMode === m.id ? 'true' : 'false'"
                :data-testid="`stage-fallback-mode-${m.id}`"
                @click="mode = m.id"
            >
                <span :data-testid="`stage-fallback-mode-label-${m.id}`">{{ m.label }}</span>
            </button>
        </div>

        <div v-if="shownMode === 'upload'" class="artifi-fallback-upload">
            <div class="artifi-fallback-surface" data-testid="stage-custom-preview">
                <img
                    v-if="customPreviewUrl"
                    :src="customPreviewUrl"
                    :alt="`Your preview image for ${colorLabel}`"
                    class="artifi-fallback-image"
                    data-testid="stage-custom-preview-image"
                />
                <img
                    v-else-if="blankUrl"
                    :src="blankUrl"
                    :alt="`${colorLabel} blank`"
                    class="artifi-fallback-image"
                    data-testid="stage-custom-preview-blank"
                />
                <GarmentSilhouette
                    v-else
                    :silhouette="silhouette"
                    :color="{ name: colorLabel, hex: colorHex }"
                    view="front"
                    class="artifi-fallback-garment"
                />
            </div>
        </div>

        <div v-else-if="shownMode === 'zones'" class="artifi-fallback-zones">
            <div class="artifi-fallback-surface artifi-fallback-surface--fixed" data-testid="stage-zone-diagram">
                <img
                    v-if="blankUrl"
                    :src="blankUrl"
                    :alt="`${colorLabel} blank`"
                    class="artifi-fallback-image"
                    data-testid="stage-zone-diagram-photo"
                />
                <GarmentSilhouette
                    v-else
                    :silhouette="silhouette"
                    :color="{ name: colorLabel, hex: colorHex }"
                    view="front"
                    class="artifi-fallback-garment"
                />
                <svg
                    class="artifi-fallback-zone-svg"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                >
                    <rect
                        v-for="mark in zoneMarks"
                        :key="mark.key"
                        :x="mark.box.leftPct"
                        :y="mark.box.topPct"
                        :width="mark.box.wPct"
                        :height="mark.box.hPct"
                        class="artifi-fallback-zone-rect"
                        vector-effect="non-scaling-stroke"
                        data-testid="stage-zone-diagram-mark"
                        :data-zone="mark.zone"
                    />
                </svg>
                <div
                    v-for="mark in zoneMarks"
                    :key="`art-${mark.key}`"
                    class="artifi-fallback-zone-art"
                    :style="boxStyle(mark.box)"
                >
                    <img
                        v-if="mark.artworkUrl"
                        :src="mark.artworkUrl"
                        alt=""
                        class="artifi-fallback-zone-img"
                    />
                    <span
                        class="artifi-fallback-zone-label"
                        data-testid="stage-zone-diagram-label"
                    >{{ mark.zoneLabel }}</span>
                </div>
                <span
                    class="artifi-fallback-zone-caption"
                    data-testid="stage-zone-diagram-caption"
                >Approximate placement</span>
            </div>
            <p
                v-if="zoneMarks.length === 0"
                class="artifi-fallback-hint"
                data-testid="stage-zone-diagram-empty"
            >
                Add a placement to see where your decoration goes.
            </p>
        </div>

        <OpenCanvasPlacement
            v-else
            :silhouette="silhouette"
            :color-name="colorName"
            :color-label="colorLabel"
            :color-hex="colorHex"
            :marks="marks"
            :busy="busy"
            @save="onCanvasSave"
        />

        <p class="artifi-fallback-visual-only" data-testid="stage-fallback-visual-only">
            Preview only: these images show shoppers how the product looks.
            Your production artwork is submitted separately and isn't changed.
        </p>
    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import GarmentSilhouette from "@/components/Storefront/GarmentSilhouette.vue";
import OpenCanvasPlacement from "./OpenCanvasPlacement.vue";

const props = defineProps({
    // style_unsupported | sku_not_configured | render_failed
    reason: { type: String, required: true },
    // Lead copy (the style-unsupported case); the render-error tile carries
    // its own message above this component.
    message: { type: String, default: null },
    colorName: { type: String, required: true },
    colorLabel: { type: String, required: true },
    colorHex: { type: String, default: null },
    silhouette: { type: String, default: "generic" },
    // The color's supplier photo: the default preview shoppers see until an
    // image is uploaded, and the base the zone diagram is drawn over.
    blankUrl: { type: String, default: null },
    // [{key, zone, zoneLabel, artworkUrl, initial, box: {leftPct, topPct, wPct, hPct}}]
    // calibrated to the silhouette DRAWING (the silhouette and the open canvas).
    marks: { type: Array, default: () => [] },
    // The same marks calibrated to a supplier PHOTO, which fills its frame
    // where the drawing sits in a pad. Null: none (the drawing's boxes serve).
    photoMarks: { type: Array, default: null },
    // The color's uploaded preview image (storefront_images, source upload).
    customPreviewUrl: { type: String, default: null },
    // {state: idle|uploading|error, message}: the open canvas waits on a save.
    status: { type: Object, default: () => ({ state: "idle", message: null }) },
    // The SuperAdmin switch for the Zone diagram and Open canvas. Default OFF.
    toolsEnabled: { type: Boolean, default: false },
});
const emit = defineEmits(["upload"]);

const MODES = [
    { id: "upload", label: "Your image" },
    { id: "zones", label: "Zone diagram" },
    { id: "canvas", label: "Open canvas" },
];
function defaultMode() {
    if (props.customPreviewUrl) return "upload";
    return (props.marks ?? []).length > 0 ? "zones" : "upload";
}

const mode = ref(defaultMode());
// What is shown. With the switch off only "Your image" exists, whatever mode
// state a previous render (or a stale tab) left behind.
const shownMode = computed(() => (props.toolsEnabled ? mode.value : "upload"));

// A different color opens on ITS best view.
watch(
    () => props.colorName,
    () => {
        mode.value = defaultMode();
    },
);

// The zone diagram's boxes follow its base: the photo's calibration over a
// supplier photo, the drawing's over the silhouette.
const zoneMarks = computed(() =>
    props.blankUrl && props.photoMarks ? props.photoMarks : props.marks,
);

const busy = computed(() => props.status?.state === "uploading");

function boxStyle(box) {
    return {
        left: `${box.leftPct}%`,
        top: `${box.topPct}%`,
        width: `${box.wPct}%`,
        height: `${box.hPct}%`,
    };
}

function onCanvasSave(blob) {
    const slug = String(props.colorName).toLowerCase().replace(/[^a-z0-9]+/g, "-");
    mode.value = "upload";
    emit("upload", blob, `${slug || "color"}-canvas.png`);
}
</script>

<style scoped>
.artifi-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    /* The Stage tile is sized by this content while the options show (the
       editor's fixed stage height capped the preview at about 190x220px). So
       nothing here is a share of a parent height, and the root neither clips
       nor scrolls: overflow-y:auto also turned overflow-x into auto, which
       cut off the zone labels at the sides. */
    min-height: 0;
    color: var(--tf-text-muted, #6b7280);
    font-size: 13px;
    text-align: center;
}
.artifi-fallback-lead {
    flex: 0 0 auto;
    margin: 0;
    max-width: none;
    color: var(--tf-text-primary, #1f2937);
    font-size: 14px;
}
.artifi-fallback-modes {
    /* overflow: hidden (rounded corners) gives a flex item an automatic
       minimum size of 0, so it was the item that shrank when the column ran
       out of height, clipping its own buttons (card #472). Never shrink. */
    flex: 0 0 auto;
    display: inline-flex;
    border: 1px solid var(--tf-border, #d1d5db);
    border-radius: 6px;
    overflow: hidden;
}
.artifi-fallback-mode {
    padding: 5px 12px;
    background: #fff;
    border: 0;
    border-right: 1px solid var(--tf-border, #d1d5db);
    font-size: 13px;
    cursor: pointer;
}
.artifi-fallback-mode:last-child {
    border-right: 0;
}
.artifi-fallback-mode.is-active {
    background: var(--tf-primary, #1f2937);
    color: #fff;
}
.artifi-fallback-upload,
.artifi-fallback-zones {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    flex: 0 0 auto;
}
.artifi-fallback-surface {
    position: relative;
    /* A DEFINITE width, never a share of the parent's height (the tile has
       none): up to ~410px wide, ~480px tall on a tall window (369x430 at 900px high), shrinking
       with the window height (41vh) and never wider than its column. The
       height follows from the aspect ratio, so the zone boxes (percentages of
       this box) stay on the photo at every size. */
    flex: 0 0 auto;
    /* The photo inside has a taller natural shape (800x1200). In a column
       flex container an aspect-ratio item's automatic minimum height is its
       content's, which stretched the surface to the photo's 2:3 shape;
       min-height 0 lets the ratio below decide the height. */
    min-height: 0;
    width: min(100%, clamp(257px, 41vh, 411px));
    aspect-ratio: 6 / 7;
    display: flex;
    align-items: center;
    justify-content: center;
}
.artifi-fallback-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
.artifi-fallback-zone-caption {
    position: absolute;
    top: 4px;
    left: 4px;
    padding: 1px 6px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.85);
    font-size: 11px;
    color: var(--tf-text-muted, #6b7280);
    pointer-events: none;
}
.artifi-fallback-garment {
    width: 100%;
    height: 100%;
}
.artifi-fallback-zone-svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}
.artifi-fallback-zone-rect {
    fill: rgba(37, 99, 235, 0.06);
    stroke: var(--tf-focus, #2563eb);
    stroke-width: 1.5;
    stroke-dasharray: 6 4;
}
.artifi-fallback-zone-art {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}
.artifi-fallback-zone-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
.artifi-fallback-zone-label {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 2px;
    white-space: nowrap;
    font-size: 11px;
    color: var(--tf-focus, #2563eb);
}
.artifi-fallback-hint,
.artifi-fallback-visual-only {
    margin: 0;
    max-width: 420px;
}
.artifi-fallback-visual-only {
    font-size: 12px;
    font-style: italic;
}
</style>
